import { BracketRoundHeader, BracketRow, ShuffleColumn } from "../styles";
import {
  getDoubleElimNodesByDepth,
  getMaxLowerDepth,
  getMaxUpperDepth,
  ShuffleBracketGraph,
  shuffleBracketGraphHasGrandFinal,
  ShuffleBracketNode,
} from "model/Bracket/ShuffleBracketGraph";
import { unshiftX } from "Utility";
import { ShuffleBracketRowMatch } from "./ShuffleBracketRowMatch";
import { depthToRoundTitleDoubleElim } from "model/Bracket/ShuffleBracketGraph";
import { BracketMatchArgs } from "../BracketMatchArgs";
import { JSX } from "react";

interface Props {
  bracketGraph: ShuffleBracketGraph;
  tournamentId: string;
  bracketMatchArgs?: BracketMatchArgs;
}

export const DoubleElimShuffleBracket = ({
  bracketGraph,
  tournamentId,
  bracketMatchArgs,
}: Props) => {
  const hasGrandFinal = shuffleBracketGraphHasGrandFinal(bracketGraph);
  const maxUpperDepth = getMaxUpperDepth(bracketGraph);
  const maxLowerDepth = getMaxLowerDepth(bracketGraph);

  const { upperNodesByDepth, lowerNodesByDepth } = getDoubleElimNodesByDepth(
    !!hasGrandFinal
      ? (bracketGraph.finalNodes[0].teamSources
          .map((s) => s.node)
          .filter((n) => !!n) as ShuffleBracketNode[])
      : bracketGraph.finalNodes
  );

  const upperSpaceColumns: number[] = [];
  for (var i = 1; i < lowerNodesByDepth.length - 1; i++) {
    if (lowerNodesByDepth[i].length !== lowerNodesByDepth[i + 1].length) {
      if (i < upperNodesByDepth.length) {
        upperSpaceColumns.push(i - upperSpaceColumns.length);
      }
    }
  }

  let upperPadding =
    lowerNodesByDepth.length -
    (upperNodesByDepth.length + upperSpaceColumns.length);
  upperPadding = upperPadding > 0 ? upperPadding : 0;
  let { nodeCells: upperNodeCells, titleCells: upperTitleCells } = mapRow(
    upperNodesByDepth,
    upperSpaceColumns,
    maxUpperDepth,
    false,
    hasGrandFinal,
    tournamentId,
    upperPadding
  );

  let lowerPadding = upperNodesByDepth.length - lowerNodesByDepth.length;
  lowerPadding = lowerPadding > 0 ? lowerPadding : 0;
  let { nodeCells: lowerNodeCells, titleCells: lowerTitleCells } = mapRow(
    lowerNodesByDepth,
    [],
    maxLowerDepth,
    true,
    hasGrandFinal,
    tournamentId,
    lowerPadding
  );

  let middleNodeCells: JSX.Element[] | undefined;
  let middleTitleCells: JSX.Element[] | undefined;
  if (hasGrandFinal) {
    const finalNode = bracketGraph.finalNodes[0];
    const middleRow = getPaddedRow(lowerNodeCells.length);

    middleNodeCells = middleRow.nodeCells;
    middleNodeCells.push(
      <ShuffleColumn>
        <ShuffleBracketRowMatch node={finalNode} tournamentId={tournamentId} />
      </ShuffleColumn>
    );
    middleTitleCells = middleRow.titleCells;
    middleTitleCells.push(
      <ShuffleColumn>
        <BracketRoundHeader isLower={false}>
          {depthToRoundTitleDoubleElim(0, 1, false, hasGrandFinal, false)}
        </BracketRoundHeader>
      </ShuffleColumn>
    );
  }

  return (
    <div style={{ padding: "10px", marginTop: "10px" }}>
      <BracketRow>{upperTitleCells}</BracketRow>
      <BracketRow>{upperNodeCells}</BracketRow>
      <br />
      <BracketRow>{middleTitleCells}</BracketRow>
      <BracketRow>{middleNodeCells}</BracketRow>
      <br />
      <BracketRow>{lowerTitleCells}</BracketRow>
      <BracketRow>{lowerNodeCells}</BracketRow>
    </div>
  );
};

const mapRow = (
  nodesByDepth: ShuffleBracketNode[][],
  spaceColumns: number[],
  maxDepth: number,
  isLower: boolean,
  hasGrandFinal: boolean,
  tournamentId: string,
  padding: number = 0
) => {
  const nodeCells: JSX.Element[] = [];
  const titleCells: JSX.Element[] = [];
  const hasMultipleFinals = nodesByDepth[0].length > 1;
  for (var i = 0; i < nodesByDepth.length; i++) {
    const currentColumn = nodesByDepth[i];
    const nextColumn =
      i < nodesByDepth.length - 1 ? nodesByDepth[i + 1] : undefined;

    // Create the node column for the current depth
    const nodeColumnContents: JSX.Element[] = [];
    currentColumn.forEach((n) => {
      nodeColumnContents.push(
        <ShuffleBracketRowMatch node={n} tournamentId={tournamentId} />
      );
    });

    nodeCells.unshift(<ShuffleColumn>{nodeColumnContents}</ShuffleColumn>);
    titleCells.unshift(
      <ShuffleColumn>
        <BracketRoundHeader isLower={isLower}>
          {depthToRoundTitleDoubleElim(
            i + 1,
            maxDepth,
            isLower,
            hasGrandFinal,
            hasMultipleFinals
          )}
        </BracketRoundHeader>
      </ShuffleColumn>
    );
    if (!!nextColumn) {
      if (spaceColumns.includes(i + 1)) {
        nodeCells.unshift(<ShuffleColumn />);
        titleCells.unshift(<ShuffleColumn />);
      }
    }
  }

  unshiftX(
    <>
      <ShuffleColumn />
    </>,
    padding,
    nodeCells
  );

  unshiftX(
    <>
      <ShuffleColumn />
    </>,
    padding,
    titleCells
  );
  return { nodeCells, titleCells };
};

const getPaddedRow = (padding: number) => {
  const nodeCells: JSX.Element[] = [];
  const titleCells: JSX.Element[] = [];
  unshiftX(<ShuffleColumn />, padding, nodeCells);
  unshiftX(<ShuffleColumn />, padding, titleCells);
  return { nodeCells, titleCells };
};
